import {
  Sheet,
  SheetTitle,
  SheetHeader,
  SheetTrigger,
  SheetContent,
  SheetDescription,
} from '@design-system/Sheet';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { cn } from '@utils/styles';
import { DotIcon } from '@/components/icons/DotIcon';
import { ScrollArea } from '@design-system/ScrollArea';
import { NotificationBellIconFilled } from '@/components/icons/NotificationBellIconFilled';
import { useNotificationViewModel } from '@/features/HomeViewModels/Notification.view.model';
import { VIMessage } from '@viome/viome-base/src/usecase/Notification/Notification.model';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@design-system/Loader';
import { Render } from '@/components/Helpers/ComponentRender/Render';

const actionNavigationMap: Record<string, string> = {
  GO_TO_HEALTH: '/health',
  GO_TO_NUTRITION: '/nutrition',
  GO_TO_SHOP: '/shop',
  GO_TO_QUESTIONNAIRE: '/questionnaires',
  GO_TO_SMP: '/smp',
  GO_TO_SUPPLEMENTS: '/nutrition/formula?product=Supplements',
  GO_TO_BB: '/boosters-and-blockers',
  GO_TO_BIOTICS: '/nutrition/formula?product=Gut+Formula',
  GO_TO_TOOTHGEL: '/nutrition/formula?product=Toothpaste+%26+Gel',
  GO_TO_CONNECTIONS: '/health/connections',
  GO_TO_LOZENGES: '/nutrition/formula?product=Oral+Lozenges',
  GO_TO_NUTRITIONTIPS: '/nutrition?scrollTo=nutritionTips',
  GO_TO_SF: '/nutrition/foods?rank=4',
  GO_TO_AF: '/nutrition/foods?rank=3',
  GO_TO_MF: '/nutrition/foods?rank=2',
  GO_TO_GUTMICROBIOME: '/health/gut-microbiome',
  GO_TO_ORALMICROBIOME: '/health/oral-microbiome',
  GO_TO_FOODS: '/nutrition/foods',
  GO_TO_ALLSCORES: '/health/score/contributing-score',
  GO_TO_INFLAMMAGING: '/health/inflammaging',
  GO_TO_BIOAGE: '/health/biological-age',
  GO_TO_BBGUT: '/boosters-and-blockers/1369',
  GO_TO_BBMOOD: '/boosters-and-blockers/1402',
  GO_TO_BBENERGY: '/boosters-and-blockers/1372',
  GO_TO_BBSUGAR: '/boosters-and-blockers/blood-sugar',
  GO_TO_BBIMMUNITY: '/boosters-and-blockers/1371',
  GO_TO_BBORAL: '/boosters-and-blockers/1400',
  GO_TO_BBHEART: '/boosters-and-blockers/1401',
  GO_TO_BBPLANT: '/boosters-and-blockers/plant-virus',
};

export const NotificationCenter = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const {
    notificationsData,
    isLoadingNotifications,
    handleMarkAsRead,
    isErrorNotifications,
    handleRefresh,
    unreadCount,
    handleRefreshUnreadCount,
  } = useNotificationViewModel(open);

  useEffect(() => {
    if (open) {
      handleRefresh();
    }
  }, [open]);

  const handleModalToggle = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  const onClickHomeNavigate = () => {
    navigate('/');
    setOpen(false);
  };

  const handleItemPress = async (item: VIMessage) => {
    const { action, id, isRead } = item;
    let navigationTarget: string | undefined;

    if (action === 'GO_TO_HOME') {
      onClickHomeNavigate();
    } else if (action === 'GO_TO_TRACKING') {
      navigate('/?trackMySample=true');
      setOpen(false);
    } else if (action === 'GO_TO_COMMUNITY') {
      navigate('/?scrollTo=community');
      setOpen(false);
    } else {
      navigationTarget = actionNavigationMap[action];
      if (typeof navigationTarget === 'string') {
        navigate(navigationTarget);
        setOpen(false);
      } else {
        onClickHomeNavigate();
      }
    }

    if (!isRead) {
      try {
        await handleMarkAsRead(id);
        await handleRefreshUnreadCount();
      } catch (error) {
        console.error('Error processing notification:', error);
      }
    }
  };

  let content = null;

  if (isLoadingNotifications) {
    content = (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (isErrorNotifications) {
    content = (
      <div className="h-full flex flex-col justify-center items-center font-sans">
        <div className="typography-h1 text-tertiary">
          Error loading notifications
        </div>
        <div className="typography-btn-medium text-tertiary">
          Please try again later
        </div>
      </div>
    );
  }

  if (notificationsData?.messages.length === 0) {
    content = (
      <div className="h-full flex flex-col justify-center items-center font-sans">
        <div className="typography-h1 text-tertiary">No new notifications</div>
        <div className="typography-btn-medium text-tertiary mt-2 text-center">
          Notifications from the last 30 days are shown
        </div>
      </div>
    );
  }

  if (notificationsData && notificationsData?.messages.length > 0) {
    content = (
      <ScrollArea>
        {notificationsData?.messages?.map((item: VIMessage) => {
          const { isRead, title, description, id, createdAt } = item || {};
          return (
            <div
              className="w-full break-all cursor-pointer"
              key={id}
              onClick={() => handleItemPress(item)}
            >
              <div className="flex flex-row items-center">
                {!isRead && (
                  <div>
                    <DotIcon
                      width={24}
                      height={24}
                      className="text-info !size-6"
                    />
                  </div>
                )}
                <div
                  className={cn(
                    'flex items-center typography-title font-sans text-primary line-clamp-1',
                    isRead && 'text-tertiary typography-body ml-6',
                  )}
                >
                  {title}
                </div>
              </div>
              <div
                className={cn(
                  'ml-6 mt-4 typography-btn-medium font-sans text-primary',
                  isRead && 'text-tertiary',
                )}
              >
                {description}
              </div>
              {createdAt && (
                <div className="typography-btn-small font-sans text-tertiary ml-6 mt-4">
                  {dayjs(createdAt).format('MMM D, YYYY')}
                </div>
              )}
              <div className="w-full py-6">
                <hr />
              </div>
            </div>
          );
        })}
      </ScrollArea>
    );
  }

  return (
    <Sheet onOpenChange={handleModalToggle} open={open}>
      <SheetTrigger asChild>
        <div className="relative inline-flex">
          <NotificationBellIconFilled
            className="!size-6 cursor-pointer"
            aria-label="My notifications"
          />
          <Render when={unreadCount > 0}>
            <div className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 border border-white rounded-full bg-red-500 text-white text-xxs font-light">
              {unreadCount}
            </div>
          </Render>
        </div>
      </SheetTrigger>

      <SheetContent modal>
        <SheetHeader className="h-full">
          <SheetTitle className="typography-h1 text-primary">
            Notifications
          </SheetTitle>

          <SheetDescription className="typography-body text-primary -ml-6 h-full">
            {content}
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
};
